/**
 * dsl-reviews.js
 * Client-side behavior for the native Dutch Skin Lab review system.
 *
 * Provides:
 *   - Filter/sort/search on the product reviews list
 *   - Lightbox for photo/video media tiles
 *   - localStorage-backed helpful/unhelpful voting (soft counter — real
 *     persistence requires a backend endpoint; see review-system/docs/BLOCKERS.md)
 *   - Review form: image/video previews + client-side downscaling before upload
 *   - GA4 dataLayer events: review_submitted, review_photo_uploaded,
 *     review_helpful_vote, review_filter_used
 *   - Toggles for collapsible form/qna wrappers
 *   - Accessibility: focus trap on lightbox, Esc to close, keyboard-navigable stars
 *
 * Ships as a theme asset. Loaded once via layout/theme.liquid.
 */
(function () {
  'use strict';

  const DOC = document;
  const STORAGE_KEY = 'dsl_review_votes_v1';
  const dataLayer = (window.dataLayer = window.dataLayer || []);
  const gtag = function () { dataLayer.push(arguments); };

  // --------------------------------------------------------------------------
  // Toggles (collapsible form/qna wrappers)
  // --------------------------------------------------------------------------
  DOC.addEventListener('click', function (e) {
    const toggle = e.target.closest('[data-dsl-toggle]');
    if (!toggle) return;
    const selector = toggle.getAttribute('data-dsl-toggle');
    const target = DOC.querySelector(selector);
    if (!target) return;
    const isOpen = !target.hasAttribute('hidden');
    if (isOpen) {
      target.setAttribute('hidden', '');
      toggle.setAttribute('aria-expanded', 'false');
    } else {
      target.removeAttribute('hidden');
      toggle.setAttribute('aria-expanded', 'true');
      const firstField = target.querySelector('input, textarea, select, button');
      if (firstField) firstField.focus();
    }
  });

  // --------------------------------------------------------------------------
  // Review list: filters + sort + search + pagination
  // --------------------------------------------------------------------------
  DOC.querySelectorAll('.dsl-reviews').forEach(function (root) {
    const list = root.querySelector('[data-dsl-review-list]');
    if (!list) return;
    const items = Array.from(list.querySelectorAll('[data-dsl-review-item]'));
    const initialLimit = parseInt(root.dataset.initialLimit, 10) || 6;
    const loadMoreBtn = root.querySelector('[data-dsl-load-more]');
    const sortSel = root.querySelector('[data-dsl-sort]');
    const ratingSel = root.querySelector('[data-dsl-rating-filter]');
    const photosChk = root.querySelector('[data-dsl-photos-only]');
    const videosChk = root.querySelector('[data-dsl-videos-only]');
    const verifiedChk = root.querySelector('[data-dsl-verified-only]');
    const keywordInp = root.querySelector('[data-dsl-keyword]');
    let visibleCount = initialLimit;

    function matches(item) {
      const card = item.querySelector('.dsl-review-card');
      if (!card) return false;
      const r = parseFloat(card.dataset.rating || '0');
      if (ratingSel && ratingSel.value !== 'all' && Math.round(r) !== parseInt(ratingSel.value, 10)) return false;
      if (photosChk && photosChk.checked && card.dataset.hasPhotos !== 'true') return false;
      if (videosChk && videosChk.checked && card.dataset.hasVideo !== 'true') return false;
      if (verifiedChk && verifiedChk.checked && card.dataset.verified !== 'true') return false;
      if (keywordInp && keywordInp.value.trim()) {
        const kw = keywordInp.value.trim().toLowerCase();
        const txt = (card.textContent || '').toLowerCase();
        if (txt.indexOf(kw) === -1) return false;
      }
      return true;
    }

    function sortItems(arr) {
      const mode = sortSel ? sortSel.value : 'newest';
      const by = {
        newest: function (a, b) {
          const da = new Date(a.querySelector('time')?.getAttribute('datetime') || 0);
          const db = new Date(b.querySelector('time')?.getAttribute('datetime') || 0);
          return db - da;
        },
        highest: function (a, b) {
          return parseFloat(b.querySelector('.dsl-review-card').dataset.rating) -
                 parseFloat(a.querySelector('.dsl-review-card').dataset.rating);
        },
        lowest: function (a, b) {
          return parseFloat(a.querySelector('.dsl-review-card').dataset.rating) -
                 parseFloat(b.querySelector('.dsl-review-card').dataset.rating);
        },
        helpful: function (a, b) {
          const ha = parseInt(a.querySelector('[data-count-up]')?.textContent || '0', 10);
          const hb = parseInt(b.querySelector('[data-count-up]')?.textContent || '0', 10);
          return hb - ha;
        }
      };
      arr.sort(by[mode] || by.newest);
    }

    function render() {
      const filtered = items.filter(matches);
      sortItems(filtered);
      list.innerHTML = '';
      filtered.slice(0, visibleCount).forEach(function (it) { list.appendChild(it); });
      if (loadMoreBtn) {
        if (filtered.length > visibleCount) loadMoreBtn.removeAttribute('hidden');
        else loadMoreBtn.setAttribute('hidden', '');
      }
    }

    [sortSel, ratingSel, photosChk, videosChk, verifiedChk].forEach(function (el) {
      if (el) el.addEventListener('change', function () {
        visibleCount = initialLimit;
        render();
        gtag('event', 'review_filter_used', {
          filter: el.dataset ? Object.keys(el.dataset)[0] : 'unknown',
          value: el.value || el.checked
        });
      });
    });
    if (keywordInp) {
      let t;
      keywordInp.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          visibleCount = initialLimit;
          render();
        }, 200);
      });
    }
    if (loadMoreBtn) loadMoreBtn.addEventListener('click', function () {
      visibleCount += initialLimit;
      render();
    });

    render();
  });

  // --------------------------------------------------------------------------
  // Lightbox
  // --------------------------------------------------------------------------
  const LIGHTBOX = DOC.getElementById('dsl-lightbox');
  const LIGHTBOX_STAGE = LIGHTBOX ? LIGHTBOX.querySelector('[data-dsl-lightbox-stage]') : null;
  let LAST_FOCUSED = null;

  function openLightbox(trigger) {
    if (!LIGHTBOX || !LIGHTBOX_STAGE) return;
    const type = trigger.getAttribute('data-media-type');
    const src = trigger.getAttribute('data-media-src');
    LIGHTBOX_STAGE.innerHTML = '';
    if (type === 'video') {
      const v = DOC.createElement('video');
      v.src = src;
      v.controls = true;
      v.autoplay = true;
      v.playsInline = true;
      LIGHTBOX_STAGE.appendChild(v);
    } else {
      const img = DOC.createElement('img');
      img.src = src;
      img.alt = '';
      LIGHTBOX_STAGE.appendChild(img);
    }
    const body = trigger.getAttribute('data-review-body');
    if (body) {
      const p = DOC.createElement('p');
      p.className = 'dsl-lightbox__caption';
      p.textContent = body;
      LIGHTBOX_STAGE.appendChild(p);
    }
    const productUrl = trigger.getAttribute('data-product-url');
    const productTitle = trigger.getAttribute('data-product-title');
    if (productUrl && productTitle) {
      const a = DOC.createElement('a');
      a.href = productUrl;
      a.className = 'dsl-lightbox__product-link';
      a.textContent = productTitle;
      LIGHTBOX_STAGE.appendChild(a);
    }
    LIGHTBOX.removeAttribute('hidden');
    LAST_FOCUSED = DOC.activeElement;
    const closeBtn = LIGHTBOX.querySelector('[data-dsl-lightbox-close]');
    if (closeBtn) closeBtn.focus();
    DOC.body.style.overflow = 'hidden';
  }
  function closeLightbox() {
    if (!LIGHTBOX || !LIGHTBOX_STAGE) return;
    LIGHTBOX.setAttribute('hidden', '');
    LIGHTBOX_STAGE.innerHTML = '';
    DOC.body.style.overflow = '';
    if (LAST_FOCUSED) LAST_FOCUSED.focus();
  }
  DOC.addEventListener('click', function (e) {
    const trig = e.target.closest('[data-dsl-lightbox]');
    if (trig) { e.preventDefault(); openLightbox(trig); return; }
    if (e.target.closest('[data-dsl-lightbox-close]')) { closeLightbox(); return; }
    if (LIGHTBOX && !LIGHTBOX.hasAttribute('hidden') && e.target === LIGHTBOX) closeLightbox();
  });
  DOC.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && LIGHTBOX && !LIGHTBOX.hasAttribute('hidden')) closeLightbox();
  });

  // --------------------------------------------------------------------------
  // Helpful voting (localStorage soft-counter)
  // --------------------------------------------------------------------------
  function getVoteStore() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
    catch (_) { return {}; }
  }
  function setVoteStore(v) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(v)); } catch (_) {}
  }
  DOC.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-vote]');
    if (!btn) return;
    const rid = btn.getAttribute('data-review-id');
    const vote = btn.getAttribute('data-vote');
    const store = getVoteStore();
    if (store[rid]) {
      btn.classList.add('dsl-review-card__vote--disabled');
      return;
    }
    store[rid] = vote;
    setVoteStore(store);
    const countEl = btn.querySelector('.dsl-review-card__vote-count');
    if (countEl) countEl.textContent = (parseInt(countEl.textContent || '0', 10) + 1).toString();
    btn.classList.add('dsl-review-card__vote--voted');
    gtag('event', 'review_helpful_vote', { review_id: rid, direction: vote });
    // TODO: POST to app-proxy endpoint for real persistence when backend is wired.
    // fetch('/apps/dsl-reviews/vote', { method:'POST', body: JSON.stringify({rid, vote}) });
  });

  // --------------------------------------------------------------------------
  // Carousel
  // --------------------------------------------------------------------------
  DOC.querySelectorAll('.dsl-review-carousel').forEach(function (root) {
    const track = root.querySelector('[data-dsl-carousel]');
    const prev = root.querySelector('[data-dsl-carousel-prev]');
    const next = root.querySelector('[data-dsl-carousel-next]');
    if (!track) return;
    const step = function () {
      const first = track.querySelector('.dsl-review-carousel__slide');
      return first ? first.getBoundingClientRect().width + 16 : 300;
    };
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
  });

  // --------------------------------------------------------------------------
  // Review form: previews + client-side resize + submit handling
  // --------------------------------------------------------------------------
  DOC.querySelectorAll('.dsl-review-form').forEach(function (form) {
    const photoInput = form.querySelector('[data-dsl-photo-input]');
    const videoInput = form.querySelector('[data-dsl-video-input]');
    const photoPrev = form.querySelector('[data-dsl-photo-previews]');
    const videoPrev = form.querySelector('[data-dsl-video-preview]');
    const uploadEndpoint = form.dataset.uploadEndpoint || '';
    const uploadNotice = form.querySelector('[data-dsl-upload-notice]');

    if (!uploadEndpoint && uploadNotice) {
      uploadNotice.removeAttribute('hidden');
      if (photoInput) photoInput.disabled = true;
      if (videoInput) videoInput.disabled = true;
    }

    function resizeImage(file, maxW) {
      return new Promise(function (resolve) {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = function () {
          const scale = Math.min(1, maxW / img.width);
          const w = Math.round(img.width * scale);
          const h = Math.round(img.height * scale);
          const c = DOC.createElement('canvas');
          c.width = w; c.height = h;
          c.getContext('2d').drawImage(img, 0, 0, w, h);
          c.toBlob(function (blob) {
            URL.revokeObjectURL(url);
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', 0.85);
        };
        img.src = url;
      });
    }

    if (photoInput && photoPrev) {
      photoInput.addEventListener('change', async function () {
        photoPrev.innerHTML = '';
        const files = Array.from(photoInput.files || []).slice(0, 5);
        for (const f of files) {
          const resized = await resizeImage(f, 1400);
          const thumb = DOC.createElement('img');
          thumb.src = URL.createObjectURL(resized);
          thumb.width = 80; thumb.height = 80;
          thumb.alt = '';
          thumb.className = 'dsl-review-form__preview';
          photoPrev.appendChild(thumb);
        }
      });
    }
    if (videoInput && videoPrev) {
      videoInput.addEventListener('change', function () {
        videoPrev.innerHTML = '';
        const f = (videoInput.files || [])[0];
        if (!f) return;
        if (f.size > 50 * 1024 * 1024) {
          videoPrev.textContent = (form.dataset.videoTooLarge || 'Video > 50MB — gebruik een kleiner bestand.');
          videoInput.value = '';
          return;
        }
        const v = DOC.createElement('video');
        v.src = URL.createObjectURL(f);
        v.controls = true;
        v.className = 'dsl-review-form__preview-video';
        videoPrev.appendChild(v);
      });
    }

    form.addEventListener('submit', function (e) {
      const status = form.querySelector('[data-dsl-form-status]');
      if (status) {
        status.removeAttribute('hidden');
        status.textContent = (form.dataset.submittingMsg || 'Bezig met versturen…');
      }
      const rating = form.querySelector('input[name="contact[rating]"]:checked');
      gtag('event', 'review_submitted', {
        product_handle: form.dataset.productHandle,
        rating: rating ? rating.value : null,
        has_photo: photoInput && photoInput.files.length > 0,
        has_video: videoInput && videoInput.files.length > 0
      });
      if (photoInput && photoInput.files.length > 0) {
        gtag('event', 'review_photo_uploaded', { count: photoInput.files.length });
      }
      // Native contact form submit proceeds (Shopify collects payload; a merchant-side
      // automation/app ingests and creates the metaobject entry in pending state).
    });
  });

  // --------------------------------------------------------------------------
  // Star input keyboard navigation (radio group already does this natively —
  // this adds visible focus indication + arrow-key support)
  // --------------------------------------------------------------------------
  DOC.querySelectorAll('[data-dsl-star-input]').forEach(function (group) {
    const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
    group.addEventListener('keydown', function (e) {
      const idx = radios.indexOf(DOC.activeElement);
      if (idx === -1) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
        e.preventDefault();
        const nxt = radios[Math.min(idx + 1, radios.length - 1)];
        nxt.checked = true; nxt.focus();
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
        e.preventDefault();
        const prv = radios[Math.max(idx - 1, 0)];
        prv.checked = true; prv.focus();
      }
    });
  });
})();
